// ** react imports
import React from 'react';
import PropTypes from 'prop-types';

// ** router imports
import { Link } from 'react-router-dom';

// ** enums imports
import { TAX_FILE_TYPE } from '../../../enums/common';
import { BADGE_THEMES, BADGE_SIZES } from '../../../enums/common';

// ** utils imports
import { getFormattedDate } from '../../../utils/Utils';

// ** component imports
import Badge from '../../../components/common/Badge';

const TaxFileCard = props => {
  // props
  const { taxFile } = props;

  // Handle missing or invalid taxFile data
  if (!taxFile) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700/60 p-5">
        <div className="text-red-500 dark:text-red-400">
          Error: Tax file data is missing or invalid
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/tax-files/details/${taxFile?._id}?type=${taxFile?.submissionType}`}
    >
      <div className="bg-white dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600/60">
        <div className="p-5">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                Tax Year ({taxFile.taxYear || 'N/A'})
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Reference #: {taxFile.referenceNumber || 'N/A'}
              </div>
            </div>
            {taxFile.fileStatus && (
              <Badge
                theme={taxFile.fileStatus}
                size={BADGE_SIZES.SM}
                text={taxFile.fileStatus}
              />
            )}
          </div>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Submission Type
                </div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  <Badge
                    theme={
                      taxFile.submissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE
                        ? BADGE_THEMES.PRIMARY
                        : BADGE_THEMES.WARNING
                    }
                    size={BADGE_SIZES.SM}
                    text={
                      taxFile.submissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE
                        ? 'Virtual'
                        : 'In-Office'
                    }
                  />
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Created Date
                </div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  {taxFile.createdAt
                    ? getFormattedDate(taxFile.createdAt)
                    : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {taxFile.assignedTo && (
          <div className="border-t border-gray-200 dark:border-gray-600/60">
            <div className="px-5 py-2.5 flex justify-between items-center-safe">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Assigned To
              </div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {taxFile.assignedTo.name || taxFile.assignedTo.email || 'N/A'}
              </div>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

TaxFileCard.propTypes = {
  taxFile: PropTypes.shape({
    taxYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    referenceNumber: PropTypes.string,
    fileStatus: PropTypes.string,
    submissionType: PropTypes.string,
    createdAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    assignedTo: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  }),
};

TaxFileCard.defaultProps = {
  taxFile: null,
};

export default TaxFileCard;
